import React, { useState, useEffect } from "react";
import { motion, MotionConfig } from "framer-motion";
import { Modal, useModal } from "./../shared/Modal";
import Loading from "./../shared/Loading";
import SelectProofRow from "./SelectProofRow";
import { findManifest, proofIsCompatible } from "./public/manifest";

const HandPickedModal = ({widget, selectedProofs, setSelectedProofs, proofsLoading = false}) => {
  const { modalOpen, setModalOpen } = useModal()
  const [proofs, setProofs] = useState([])
  const [newSelection, setNewSelection] = useState(selectedProofs)
  const [loading, setLoading] = useState(false);

  // Honor the widget's manifest (e.g. HeroQuote = text only) so users
  // can't add a proof the widget can't render.
  const manifest = findManifest(widget.kind);
  const isCompatible = (proof) => !manifest || proofIsCompatible(manifest, proof);

  const fetchProofs = async () => {
    setLoading(true)
    try {
      const response = await fetch(`/api/v1/widgets/${widget.uuid}/available_proofs`);
      const data = await response.json();

      const ids = new Set(selectedProofs.map(item => item.id))
      setProofs(data.proofs.filter(item => !ids.has(item.id)));
      setNewSelection(selectedProofs)
    } finally {
      setLoading(false)
    }
  }

  const addNewSelection = (proof) => {
    setProofs(proofs.filter((p) => p.uuid !== proof.uuid))
    setNewSelection([...newSelection, proof])
  }
  const removeNewSelection = (proof) => {
    setProofs([...proofs, proof])
    setNewSelection(newSelection.filter((p) => p.uuid !== proof.uuid))
  }

  const cancel = () => {
    setModalOpen(false)
    setNewSelection(selectedProofs)
  }

  const saveSelection = () => {
    setSelectedProofs(newSelection)
    setModalOpen(false)
  }

  useEffect(() => {
    if (modalOpen) {
      fetchProofs()
    }
  }, [modalOpen]);

  return <>
    <button
      type="button"
      onClick={() => setModalOpen(true)}
      disabled={proofsLoading}
      className={`zz-btn sm blue ${proofsLoading ? "opacity-60 cursor-default" : ""}`}
    >
      {proofsLoading ? "loading proofs…" : `select proofs (${selectedProofs.length})`}
    </button>
    { modalOpen && <Modal modalOpen={modalOpen} setModalOpen={setModalOpen}>
      <div className="relative px-1 sm:px-4 pt-2 pb-4">
        <div className="mb-5">
          <p className="zz-hand text-2xl sm:text-3xl text-ssblue leading-none">pick what matters —</p>
          <h2 className="zz-display text-3xl sm:text-4xl font-black leading-[0.95] mt-1">
            select <span className="zz-highlight yellow">proofs</span>.
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Tap to move a proof between available and selected. Save when you're happy.
          </p>
        </div>

        { loading && <div className='mt-10 flex w-full items-center justify-center mx-auto'><Loading /></div> }

        <div className="flex flex-col md:flex-row gap-4 sm:gap-5">
          <div className="w-full md:w-1/2 flex flex-col min-w-0">
            <div className="flex items-baseline justify-between mb-2">
              <p className="zz-hand text-xl text-ssgreen leading-none">available —</p>
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{loading ? "…" : proofs.length}</span>
            </div>
            <div className="zz-panel p-3 sm:p-4 flex flex-col gap-2 max-h-[35vh] md:max-h-[60vh] overflow-y-auto">
              {loading ? (
                <p className="text-sm text-slate-500 italic text-center py-8">loading proofs…</p>
              ) : proofs.length === 0 ? (
                <p className="text-sm text-slate-500 italic text-center py-8">nothing left to pick.</p>
              ) : (
                proofs.map((proof, index) => (
                  <SelectProofRow
                    key={proof.uuid}
                    initProof={proof}
                    initSelected={false}
                    addNewSelection={addNewSelection}
                    incompatible={!isCompatible(proof)}
                    index={index}
                  />
                ))
              )}
            </div>
          </div>

          <div className="w-full md:w-1/2 flex flex-col min-w-0">
            <div className="flex items-baseline justify-between mb-2">
              <p className="zz-hand text-xl text-ssblue leading-none">selected —</p>
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{newSelection.length}</span>
            </div>
            <div className="zz-panel p-3 sm:p-4 flex flex-col gap-2 max-h-[35vh] md:max-h-[60vh] overflow-y-auto">
              {newSelection.length === 0 ? (
                <p className="text-sm text-slate-500 italic text-center py-8">nothing picked yet — tap any proof on the left.</p>
              ) : (
                newSelection.map((proof, index) => (
                  <SelectProofRow
                    key={proof.uuid}
                    initProof={proof}
                    initSelected={true}
                    removeNewSelection={removeNewSelection}
                    incompatible={!isCompatible(proof)}
                    index={index}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" onClick={cancel} className="zz-link muted text-sm">
            cancel
          </button>
          <button type="button" onClick={saveSelection} className="zz-btn blue">
            save selection ✓
          </button>
        </div>
      </div>
    </Modal> }
  </>
}

export default HandPickedModal;
